import useCheckUnsavedChanges from 'core/custom-hooks/use-check-unsaved-changes.hook';
import { useDeviceTypes } from 'core/contexts/device-types/hooks/use-device-types.hook';
import { useTranslation } from 'core/i18n/i18n';
import { showErrorNotification } from 'core/utils/notification-service';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OperatorCustomNotification } from 'modules/settings/pods/settings-operator-custom-notifications/operator-custom-notification.vm';
import {
    withResettable,
    withThresholdField,
} from 'modules/settings/components/custom-notifications/custom-notification-threshold.business';
import { getCustomNotificationNameValidationCode } from 'modules/settings/components/custom-notifications/custom-notification-name-error.business';
import { usePopupManager } from 'react-popup-manager';
import { useParams } from 'react-router-dom';
import { useOperatorCustomNotificationsNavigation } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-navigation.hook';
import { NEW_NOTIFICATION_SEGMENT } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.constants';
import {
    OPERATOR_CUSTOM_NOTIFICATION_FORM_DEFAULT_STATE,
    OPERATOR_CUSTOM_NOTIFICATION_FORM_UNAVAILABLE_KEY,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.constants';
import {
    mapDeviceTypeOptions,
    mapTriggerOptions,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/operator-trigger-options.mapper';
import {
    getServerRejectedFieldValidity,
    getOperatorCustomNotificationFormValidationMessages,
    getOperatorCustomNotificationFormValidation,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.validation';
import {
    hasOperatorFormChanges,
    requiresCounterResetConfirmation,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.business';
import { CounterResetConfirmationModalComponent } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/components/counter-reset-confirmation-modal';
import {
    getUnresolvedTriggerIds,
    hasUnavailableTriggerSelection,
    hydrateTriggerSelection,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/operator-trigger-hydration.business';
import { useOperatorCustomNotificationSaver } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/hooks/use-operator-custom-notification-saver.hook';
import { retainTriggersOfDeviceTypes } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/operator-trigger-selection.business';
import { useOperatorTriggerCatalogue } from 'modules/settings/pods/settings-operator-custom-notifications/service/hooks/use-operator-trigger-catalogue.hook';
import { useOperatorCustomNotificationDetail } from 'modules/settings/pods/settings-operator-custom-notifications/service/hooks/use-operator-custom-notification-detail.hook';
import {
    buildOperatorCustomNotificationPayload,
    mapComparableOperatorFormState,
    mapOperatorCustomNotificationToFormState,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.mapper';
import {
    DraftSelectionField,
    NotificationLevel,
    NotificationThresholdFormState,
    OperatorCustomNotificationFormMode,
    OperatorCustomNotificationFormState,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.model';

const keepOpenUnlessTheNameWasRejected = (error: Error) => {
    if (!getCustomNotificationNameValidationCode(error)) {
        throw error;
    }
};

export const useOperatorCustomNotificationForm = () => {
    const { t } = useTranslation();
    const { customAlarmDefinitionId } = useParams();
    const { navigateToList } = useOperatorCustomNotificationsNavigation();
    const { deviceTypes, isLoading: isLoadingDeviceTypes } = useDeviceTypes();
    const [form, setForm] = useState<OperatorCustomNotificationFormState>(
        OPERATOR_CUSTOM_NOTIFICATION_FORM_DEFAULT_STATE
    );
    const [thresholdsTouched, setThresholdsTouched] = useState<boolean>(false);

    const mode: OperatorCustomNotificationFormMode =
        customAlarmDefinitionId === NEW_NOTIFICATION_SEGMENT ? 'add' : 'edit';

    const { notification, isLoadingNotification, hasFailedToLoadNotification } = useOperatorCustomNotificationDetail({
        notificationId: customAlarmDefinitionId,
        enabled: mode === 'edit',
    });

    useEffect(() => {
        if (hasFailedToLoadNotification) {
            showErrorNotification(t(OPERATOR_CUSTOM_NOTIFICATION_FORM_UNAVAILABLE_KEY));
        }
    }, [hasFailedToLoadNotification, t]);

    const seededForm = useMemo(
        () => (notification ? mapOperatorCustomNotificationToFormState(notification) : undefined),
        [notification]
    );

    const baselineComparableState = useMemo(
        () => mapComparableOperatorFormState(seededForm ?? OPERATOR_CUSTOM_NOTIFICATION_FORM_DEFAULT_STATE),
        [seededForm]
    );

    const [seededFrom, setSeededFrom] = useState<OperatorCustomNotification>();

    if (seededForm && notification !== seededFrom) {
        setSeededFrom(notification);
        setForm(seededForm);
    }

    const { catalogue, isLoadingCatalogue, hasFailedToLoadCatalogue } = useOperatorTriggerCatalogue(
        form.deviceTypes.selectedIds
    );

    const deviceTypeOptions = useMemo(() => mapDeviceTypeOptions(deviceTypes), [deviceTypes]);
    const triggerOptions = useMemo(
        () => mapTriggerOptions(catalogue, deviceTypes, t, notification?.trackedTriggers),
        [catalogue, deviceTypes, t, notification]
    );

    const hasFormChanges = hasOperatorFormChanges(form, baselineComparableState);
    const validation = getOperatorCustomNotificationFormValidation(form);

    const isLeaving = useRef<boolean>(false);
    const exitTimer = useRef<ReturnType<typeof globalThis.setTimeout>>();

    useEffect(() => () => globalThis.clearTimeout(exitTimer.current), []);

    const leaveToList = () => {
        isLeaving.current = true;
        navigateToList();
    };

    const unsavedData = useCheckUnsavedChanges(leaveToList);

    const leaveAfterSave = () => {
        isLeaving.current = true;
        unsavedData.setIsUnsavedChanges(false);
        exitTimer.current = globalThis.setTimeout(navigateToList, 0);
    };

    const {
        isSubmitting,
        nameCustomValidationCode,
        resetNameCustomValidationCode,
        addNotification,
        editNotification,
        editNotificationAsync,
    } = useOperatorCustomNotificationSaver({
        onSuccess: leaveAfterSave,
    });
    const popupManager = usePopupManager();

    const trackedTriggers = hydrateTriggerSelection(form.triggers.selectedIds, catalogue);
    const unresolvedTriggerIds = getUnresolvedTriggerIds(form.triggers.selectedIds, catalogue);

    // Saving either would overwrite the stored definition with what the form never loaded.
    const hasLoadedWhatItEdits = mode === 'add' || notification !== undefined;
    const canSubmit =
        validation.formValid && hasFormChanges && unresolvedTriggerIds.length === 0 && hasLoadedWhatItEdits;

    useEffect(() => {
        unsavedData.setIsUnsavedChanges(!isLeaving.current && hasFormChanges);
    }, [hasFormChanges, unsavedData]);

    const setField = <TField extends keyof OperatorCustomNotificationFormState>(
        field: TField,
        value: OperatorCustomNotificationFormState[TField]
    ) => setForm((previous) => ({ ...previous, [field]: value }));

    const setName = (name: string) => {
        resetNameCustomValidationCode();
        setField('name', name);
    };

    const handleSubmit = () => {
        resetNameCustomValidationCode();

        if (!canSubmit) {
            return;
        }

        const payload = buildOperatorCustomNotificationPayload(form, trackedTriggers);

        if (mode === 'add') {
            addNotification(payload);
        } else if (customAlarmDefinitionId && notification) {
            const update = { payload, notificationId: customAlarmDefinitionId };

            if (requiresCounterResetConfirmation(payload, notification)) {
                popupManager.open(CounterResetConfirmationModalComponent, {
                    onConfirm: () => editNotificationAsync(update).catch(keepOpenUnlessTheNameWasRejected),
                });
            } else {
                editNotification(update);
            }
        }
    };

    const setThresholdField = <TField extends keyof NotificationThresholdFormState>(
        level: NotificationLevel,
        field: TField,
        value: NotificationThresholdFormState[TField]
    ) => {
        setThresholdsTouched(true);
        setForm((previous) => withThresholdField(previous, level, field, value));
    };

    const setDraftIds = (field: DraftSelectionField, draftIds: string[]) =>
        setForm((previous) => ({ ...previous, [field]: { ...previous[field], draftIds } }));

    const applySelection = (field: DraftSelectionField, selectedIds: string[]) =>
        setForm((previous) => {
            const applied = { ...previous, [field]: { draftIds: selectedIds, selectedIds } };

            if (field !== 'deviceTypes') {
                return applied;
            }

            const retainedTriggerIds = retainTriggersOfDeviceTypes(previous.triggers.selectedIds, selectedIds);

            return { ...applied, triggers: { draftIds: retainedTriggerIds, selectedIds: retainedTriggerIds } };
        });

    const setResettable = (isResettable: boolean) => setForm((previous) => withResettable(previous, isResettable));

    const handleClose = () => unsavedData.showUnsavedChangesIfNeededOrClose(leaveToList);

    const hasUnavailableTriggers = hasUnavailableTriggerSelection(
        unresolvedTriggerIds,
        !isLoadingCatalogue && !hasFailedToLoadCatalogue
    );

    const validationMessages = getOperatorCustomNotificationFormValidationMessages(t);
    const fieldValidity = getServerRejectedFieldValidity({
        nameCustomValidationCode,
        hasUnavailableTriggers,
    });

    return {
        mode,
        form,
        thresholdsTouched,
        isLoadingNotification,
        deviceTypeOptions,
        triggerOptions,
        isLoadingDeviceTypes: Boolean(isLoadingDeviceTypes),
        isLoadingCatalogue,
        canSubmit,
        isSubmitting,
        nameCustomValidationCode,
        fieldValidity,
        windowDaysValidity: validation.windowDaysValidity,
        validationMessages,
        setName,
        handleSubmit,
        setField,
        setDraftIds,
        applySelection,
        setThresholdField,
        setResettable,
        handleClose,
    };
};
